/*
  Настройки организации — §3.1.5, §6.7.2, §10.6.

  Четыре группы, и каждая меняет поведение системы. Реквизиты взыскателя: без них
  заявление вернут, и экран показывает недостающее списком, который считает
  сервер — тем же самым, каким проверяет шаблонизатор. Тарифы: пустое поле
  честнее нуля, потому что гейт отдачи тогда отвечает «посчитать нечем», а не
  выдаёт правдоподобную цифру. Режим давности: он определяет, какая сумма
  уйдёт в суд, поэтому вместе с ним сохраняется, кто и когда его выбрал.

  🔴 Группы разложены по вкладкам 09.09.2026, решение владельца по итогам
  осмотра. До этого все девятнадцать полей шли одной простынёй в четыре карточки
  подряд, а внизу каждой стояла своя кнопка «Сохранить» - три кнопки, которые
  отличались только подписью, и понять, какая что сохраняет, можно было лишь по
  тому, в какой карточке она лежит. Это тот же дефект, который в Арбитр ДА
  21.08.2026 пришлось переделывать после показа: «двадцать семь настроек одной
  простынёй».

  Экран остался контейнером: он грузит три ответа сервера, держит сообщение об
  ошибке и об успехе, а каждая вкладка знает только про свою форму и ничего не
  грузит сама.
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { fieldErrors } from '../api/fieldErrors.js'
import { Loading, Notice } from '../components/Common.jsx'
import useSettingsSave from '../components/useSettingsSave.js'
import Tabs, { TabPanel } from '../components/Tabs.jsx'
import SettingsConnectorKeys from '../components/SettingsConnectorKeys.jsx'
import SettingsCourtSections from '../components/SettingsCourtSections.jsx'
import SettingsInstalment from '../components/SettingsInstalment.jsx'
import SettingsLimitation from '../components/SettingsLimitation.jsx'
import SettingsOrganisation from '../components/SettingsOrganisation.jsx'
import SettingsRequisites from '../components/SettingsRequisites.jsx'
import SettingsTariffs from '../components/SettingsTariffs.jsx'
import SettingsChannels from '../components/SettingsChannels.jsx'
import UsersSection from '../components/UsersSection.jsx'

const ORGANISATION_TABS = [
  { key: 'organisation', title: 'Организация' },
  { key: 'requisites', title: 'Реквизиты' },
  { key: 'tariffs', title: 'Тарифы' },
  { key: 'instalment', title: 'Рассрочка' },
  { key: 'limitation', title: 'Взыскание' },
]

const ACCESS_TABS = [
  { key: 'voice', title: 'Связь' },
  { key: 'connector', title: 'Обмен с 1С' },
  { key: 'users', title: 'Сотрудники' },
]

const COURTS_TAB = { key: 'courts', title: 'Судебные участки' }

/*
  🔴 Вкладки зависят от роли, и раздел открыт трём разрешениям сразу
  (18.09.2026). Первые четыре вкладки - то, из чего собирается заявление и что
  решает размер требования: их правит `manage_organisation`, то есть
  руководитель, юрист и агент сопровождения. Связь и сотрудники остались за
  `manage_settings` - это управление доступом и подключением станции, работа
  руководителя. Справочник участков ведёт тот, кто подаёт, - `build_documents`.

  Грузится ровно то, что вкладки покажут: лишний запрос уронил бы экран отказом
  сервера ещё до первого взгляда - именно так и было до 18.09.2026, когда юрист
  не видел здесь ничего, кроме участков.
*/
export default function SettingsPage({ profile }) {
  const [organisation, setOrganisation] = useState(null)
  const [requisites, setRequisites] = useState(null)
  const [economics, setEconomics] = useState(null)
  const [voice, setVoice] = useState(null)
  const [sms, setSms] = useState(null)
  // Ошибка, отметка «сохранено» и защита от двойного нажатия - в `useSettingsSave`.
  const { error, setError, saved, setSaved, save, saving } = useSettingsSave()
  // Выданный ключ обмена показывается один раз и живёт здесь, а не во вкладке: вкладка
  // при переключении не рисуется, и ключ пропадал (аудит 26.10.2026, причина 7).
  const [issuedKey, setIssuedKey] = useState(null)
  // Причины отказа по полям (аудит 26.10.2026, причина 6): формы ставят их под самими полями.
  const problems = fieldErrors(error)

  const permissions = profile.permissions ?? []
  const managesOrganisation = permissions.includes('manage_organisation')
  const managesAccess = permissions.includes('manage_settings')
  const keepsDirectory = permissions.includes('build_documents')
  const tabs = [
    ...(managesOrganisation ? ORGANISATION_TABS : []),
    ...(managesAccess ? ACCESS_TABS : []),
    ...(keepsDirectory ? [COURTS_TAB] : []),
  ]
  /*
    Вкладка открывается по якорю адреса: `/settings#courts` ведёт сразу в
    справочник участков. Заведено 18.09.2026 вместе с чек-листом готовности - он
    ссылается на конкретное место, и ссылка «Настройки - Судебные участки»,
    приводящая на «Организацию», хуже отсутствия ссылки: человек считает, что
    попал куда надо, и ищет поле там, где его нет.

    Читается один раз при создании состояния, а не эффектом: якорь - это то, с
    чем человек пришёл, а не то, чем он дальше управляет. Эффект перебивал бы
    его собственный выбор вкладки при каждой перерисовке.
  */
  const [tab, setTab] = useState(() => {
    const anchor = window.location.hash.replace('#', '')
    if (tabs.some((item) => item.key === anchor)) return anchor
    return tabs.length ? tabs[0].key : COURTS_TAB.key
  })

  useEffect(() => {
    if (!managesOrganisation) return
    Promise.all([api.organisation(), api.requisites(), api.economics()])
      .then(([org, req, eco]) => {
        setOrganisation(org)
        setRequisites(req)
        setEconomics(eco)
      })
      .catch(setError)
  }, [managesOrganisation, setError])

  useEffect(() => {
    if (!managesAccess) return
    // Оба канала одним заходом: они живут на одной вкладке, и второй запрос
    // вслед за первым означал бы, что форма рассылки появляется позже формы
    // линии - на глаз это выглядит как незагрузившийся экран.
    Promise.all([api.voiceChannel(), api.smsChannel()])
      .then(([line, mailing]) => {
        setVoice(line)
        setSms(mailing)
      })
      .catch(setError)
  }, [managesAccess, setError])

  /*
    Ждём только то, что роли положено. Ошибка при этом показывается, а не
    проглатывается: без общей проверки отказ на чтении линии связи оставлял бы
    экран в вечном «Читаем настройки» - человек видел бы бесконечную загрузку и
    не узнал бы, что произошло (правило 3 скилла `frontend`).
  */
  const awaited =
    (managesOrganisation && (!organisation || !requisites || !economics)) ||
    (managesAccess && (!voice || !sms))
  if (error && awaited) return <Notice error={error} />
  if (awaited) return <Loading what="Читаем настройки" />

  async function saveOrganisation(body) {
    const updated = await save(() => api.saveOrganisation(body), 'Карточка организации')
    if (updated) {
      setOrganisation(updated)
      // Наименование и ИНН входят в список обязательных реквизитов: после их
      // правки сервер считает недостающее заново.
      setRequisites(await api.requisites())
    }
  }

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">
            {managesOrganisation || managesAccess ? 'Настройки организации' : 'Справочники'}
          </h1>
          <div className="head__note">
            {organisation ? organisation.name : 'Судебные участки и дома под ними'}
          </div>
        </div>
      </header>

      {/*
        Переход на другую вкладку гасит и отметку об успехе, и ошибку: без
        этого «Режим давности - сохранено» висело над формой сотрудников, и
        человек читал его как отчёт о том, что он делает сейчас (осмотр
        09.09.2026). Это правило 4 чек-листа формы из скилла `frontend`,
        применённое к вкладкам.
      */}
      <Tabs
        tabs={tabs}
        active={tab}
        onChange={(key) => {
          setTab(key)
          setSaved(null)
          setError(null)
        }}
      />

      {/* Сообщения стоят под вкладками и над формой - там же, где действие. */}
      {error ? <Notice error={error} /> : null}
      {saved ? <Notice kind="ok">{saved} - сохранено.</Notice> : null}

      {/* Лицензия не оплачена - настройки видны, но не правятся (аудит 26.10.2026,
          причина 2): в каждой форме поля и кнопка сохранения серые (`Lock`), вкладки и
          листание остаются рабочими, причина - в плашке каркаса. `saving` - идёт
          сохранение: форма серая до ответа (причина 4). Причины отказа по полям
          (`problems`) встают под самими полями (причина 6). */}
      <TabPanel tabKey="organisation" active={tab}>
        <section className="card">
          <SettingsOrganisation
            organisation={organisation}
            onSave={saveOrganisation}
            saving={saving}
            problems={problems}
          />
        </section>
      </TabPanel>

      <TabPanel tabKey="requisites" active={tab}>
        <section className="card">
          <SettingsRequisites
            requisites={requisites}
            saving={saving}
            problems={problems}
            onSave={async (body) => {
              const updated = await save(() => api.saveRequisites(body), 'Реквизиты')
              if (updated) setRequisites(updated)
            }}
          />
        </section>
      </TabPanel>

      <TabPanel tabKey="tariffs" active={tab}>
        <section className="card">
          <SettingsTariffs
            economics={economics}
            saving={saving}
            problems={problems}
            onSave={async (body) => {
              const updated = await save(() => api.saveEconomics(body), 'Тарифы')
              if (updated) setEconomics(updated)
            }}
          />
        </section>
      </TabPanel>

      {/* Условия рассрочки грузятся и сохраняются самой вкладкой (13.10.2026). */}
      <TabPanel tabKey="instalment" active={tab}>
        <SettingsInstalment />
      </TabPanel>

      <TabPanel tabKey="limitation" active={tab}>
        <section className="card">
          <SettingsLimitation
            organisation={organisation}
            saving={saving}
            onChange={async (mode) => {
              const updated = await save(() => api.limitationMode(mode), 'Режим давности')
              if (updated) setOrganisation(updated)
            }}
          />
        </section>
      </TabPanel>

      <TabPanel tabKey="voice" active={tab}>
        <SettingsChannels
          voice={voice}
          sms={sms}
          error={error}
          onSwitch={() => {
            setSaved(null)
            setError(null)
          }}
          onSaveVoice={async (body) => {
            const updated = await save(() => api.saveVoiceChannel(body), 'Линия связи')
            if (updated) setVoice(updated)
            // Форма очищает поле пароля только при удаче: при отказе сервера
            // введённое остаётся, чтобы не набирать его заново.
            return updated
          }}
          onSaveSms={async (body) => {
            const updated = await save(() => api.saveSmsChannel(body), 'Рассылка')
            if (updated) setSms(updated)
            return updated
          }}
        />
      </TabPanel>

      {/*
        Вкладка грузит перечень ключей сама, а не из общего запроса страницы:
        ключи нужны одной вкладке из семи, и тянуть их при каждом открытии
        настроек значит платить запросом за то, на что чаще всего не смотрят.
      */}
      <TabPanel tabKey="connector" active={tab}>
        <section className="card">
          <SettingsConnectorKeys issued={issuedKey} onIssued={setIssuedKey} />
        </section>
      </TabPanel>

      <TabPanel tabKey="users" active={tab}>
        <UsersSection profile={profile} onError={setError} />
      </TabPanel>

      <TabPanel tabKey={COURTS_TAB.key} active={tab}>
        <SettingsCourtSections />
      </TabPanel>
    </>
  )
}
